import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { loginUser, rememberMeUtils, getFavorites } from '../api/client';
import { useAppStore } from '../store/appStore';
import { useI18n } from '../i18n';

export function LoginPage() {
  const navigate = useNavigate();
  const { setUser, user, setFavorites } = useAppStore();
  const { t, language } = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Check for remembered email on mount
  useEffect(() => {
    const rememberedEmail = rememberMeUtils.getRememberedEmail();
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  // Ako je korisnik već prijavljen, NE preusmeravaj automatski
  // Korisnik može pristupiti login stranici dok je ulogovan
  // useEffect(() => {
  //   if (user) {
  //     navigate('/');
  //   }
  // }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Email je case insensitive
    const normalizedEmail = email.toLowerCase().trim();

    if (!normalizedEmail || !password) {
      setError(language === 'sr' ? 'Unesite email i lozinku' : 'Enter email and password');
      setIsLoading(false);
      return;
    }

    try {
      // Autentifikacija preko PHP backend-a
      const result = await loginUser(normalizedEmail, password, rememberMe);

      if (result.success && result.user) {
        setUser({
          id: result.user.id,
          email: result.user.email,
          name: result.user.name,
          emailVerified: result.user.emailVerified,
          type: result.user.userType || result.user.type,
          role: result.user.role || 'user',
          tokenBalance: result.user.tokenBalance,
        });
        
        // Load user's favorites from API
        try {
          const favResponse = await getFavorites();
          if (favResponse.success && favResponse.favorites) {
            const favoriteIds = favResponse.favorites.map((fav: any) => String(fav.id || fav.property_id));
            setFavorites(favoriteIds);
          }
        } catch (favErr) {
          console.error('Error loading favorites:', favErr);
        }

        if (result.needsVerification) {
          setError(language === 'sr'
            ? 'Vaš nalog nije verifikovan. Proverite email za verifikacioni link.'
            : 'Your account is not verified. Check your email for verification link.');
          setIsLoading(false);
          return;
        }

        setSuccess(language === 'sr' ? 'Uspešna prijava!' : 'Login successful!');
        // Redirect na profil posle uspešnog logovanja
        setTimeout(() => navigate('/profil'), 500);
        return;
      }

      setError(result.error || t.auth.invalidCredentials);
    } catch (err) {
      setError(t.errors.generic);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[420px]"
      >
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {t.auth.loginTitle}
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              {language === 'sr' ? 'Unesite Vaše podatke' : 'Enter your credentials'}
            </p>
          </div>



          {/* Alerts */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm">
              {success}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.auth.email}
                className="w-full pl-12 pr-4 py-3.5 border-0 border-b-2 border-slate-200 dark:border-slate-600 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors"
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.auth.password}
                className="w-full pl-12 pr-12 py-3.5 border-0 border-b-2 border-slate-200 dark:border-slate-600 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label={showPassword ? (language === 'sr' ? 'Sakrij lozinku' : 'Hide password') : (language === 'sr' ? 'Prikaži lozinku' : 'Show password')}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            </div>

            {/* Remember me & Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600 dark:text-slate-400">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                />
                <span>{language === 'sr' ? 'Zapamti me' : 'Remember me'}</span>
              </label>
              <Link 
                to="/zaboravljena-lozinka" 
                className="text-sm text-amber-500 hover:text-amber-600 font-medium hover:underline"
              >
                {t.auth.forgotPassword}
              </Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-all transform hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {language === 'sr' ? 'Prijava...' : 'Signing in...'}
                </span>
              ) : (
                t.auth.loginButton
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700 text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t.auth.noAccount}{' '}
              <Link to="/registracija" className="text-amber-500 hover:text-amber-600 font-semibold hover:underline">
                {t.auth.registerButton}
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
